import { watch } from "vue";
import { useAuthStore } from "@/stores/auth";
import { useNavigationLoaderStore } from "@/stores/navigationLoader";

const DEFAULT_SLOW_AFTER_MS = 5_000;
const DEFAULT_TIMEOUT_MS = 60_000;

type WaitForStateOptions = {
  /** `onSlow` is called once if the state is still not ready after this long */
  slowAfterMs?: number;
  onSlow?: () => void;
  /** give up after this long */
  timeoutMs?: number;
};

/**
 * Resolves `true` once `isReady` evaluates to `true`, or `false` if it never does within
 * `timeoutMs`. Never rejects, so a slow connection can't break a navigation.
 *
 * Uses an immediate watcher instead of a plain condition + subscribe, which
 * closes the race where the state flips to ready before the subscription is
 * registered (previously that could hang a navigation forever).
 */
export function waitForState(
  isReady: () => boolean,
  opts: WaitForStateOptions = {},
): Promise<boolean> {
  const { slowAfterMs = DEFAULT_SLOW_AFTER_MS, onSlow, timeoutMs = DEFAULT_TIMEOUT_MS } = opts;

  return new Promise<boolean>((resolve) => {
    let settled = false;
    let unwatch: (() => void) | undefined;
    const slowTimer = onSlow ? setTimeout(onSlow, slowAfterMs) : undefined;
    const timer = setTimeout(() => {
      console.error("[StoreReadiness] Timed out waiting for state to settle");
      finish(false);
    }, timeoutMs);

    function finish(ready: boolean) {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      clearTimeout(slowTimer);
      unwatch?.();
      resolve(ready);
    }

    unwatch = watch(isReady, (ready) => ready && finish(true), { immediate: true });
    // the immediate callback can run before `unwatch` is assigned
    if (settled) unwatch();
  });
}

/**
 * Waits for auth and config stores to settle before the router proceeds.
 * One shared deadline for all of them. Resolves `false` if they never settle.
 */
export async function waitForAuthReady(): Promise<boolean> {
  const authStore = useAuthStore();
  const navigationLoaderStore = useNavigationLoaderStore();

  const isReady = () =>
    authStore.initialized &&
    authStore.entity.config.publicLoaded &&
    (!authStore.is.loggedIn() || authStore.entity.config.privateLoaded);

  try {
    return await waitForState(isReady, {
      onSlow: () => (navigationLoaderStore.slowConnection = true),
    });
  } finally {
    navigationLoaderStore.slowConnection = false;
  }
}
